import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { MobileContainer } from '@/components/layout/MobileContainer';
import { Card } from '@/components/ui/card';
import { LEARNING_CONTENT } from '@/data/learningData';
import { MODULES } from '@/data/moduleData';
import { useGameStore } from '@/store/useGameStore';
import { motion } from 'framer-motion';
import { CheckCircle2, Clock, Lock, PlayCircle } from 'lucide-react';
import { useLangNavigate, useLocalized, useT } from '@/i18n';

export default function ModulesScreen() {
    const navigate = useLangNavigate();
    const t = useT();
    const modules = useLocalized(MODULES);
    const { moduleProgress } = useGameStore();

    const getModuleStatus = (module) => {
        const progress = moduleProgress[module.id];
        if (progress?.status === 'completed') return 'completed';
        if (module.is_locked) return 'locked';
        return 'available';
    };

    const handleModuleClick = (module) => {
        const status = getModuleStatus(module);
        if (status === 'locked') return;

        if (status === 'completed') {
            navigate(`/results/${module.id}`);
        } else if (LEARNING_CONTENT[module.id]) {
            navigate(`/learn/${module.id}`);
        } else {
            navigate(`/module/${module.id}`);
        }
    };

    return (
        <MobileContainer noPadding>
            <div className="flex-1 flex flex-col overflow-y-auto scrollbar-hide">
                {/* Header */}
                <div className="px-5 pt-6 pb-4">
                    <motion.h1
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-2xl font-extrabold font-heading text-foreground mb-1"
                    >
                        {t('ui.modules.title')}
                    </motion.h1>
                    <p className="text-sm text-muted-foreground font-body">
                        {t('ui.modules.subtitle')}
                    </p>
                </div>

                {/* Modules Grid (2 columns) */}
                <div className="px-5 grid grid-cols-2 gap-3 pb-8">
                    {modules.map((module, index) => {
                        const status = getModuleStatus(module);
                        const isLocked = status === 'locked';
                        const isCompleted = status === 'completed';

                        return (
                            <motion.div
                                key={module.id}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.1 + index * 0.05 }}
                            >
                                <Card
                                    className={`relative aspect-[4/5] overflow-hidden rounded-2xl flex flex-col justify-end transition-all duration-200 cursor-pointer ${isLocked
                                        ? 'opacity-60 grayscale cursor-not-allowed'
                                        : 'hover:shadow-card active:scale-[0.98]'
                                        }`}
                                    onClick={() => handleModuleClick(module)}
                                >
                                    {/* Thumbnail */}
                                    <img
                                        src={module.thumbnail}
                                        alt={module.title}
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />

                                    {/* Overlay */}
                                    <div className={`absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent ${isCompleted ? 'bg-success/10' : ''
                                        }`} />

                                    {/* Status Icon */}
                                    <div className="absolute top-3 left-3 z-20">
                                        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${isLocked
                                            ? 'bg-white/20 text-white/50'
                                            : isCompleted
                                                ? 'bg-success text-white'
                                                : 'bg-primary text-white shadow-lg'
                                            }`}>
                                            {isLocked ? (
                                                <Lock size={16} />
                                            ) : isCompleted ? (
                                                <CheckCircle2 size={18} />
                                            ) : (
                                                <PlayCircle size={18} />
                                            )}
                                        </div>
                                    </div>

                                    <div className="relative z-10 p-4 w-full">
                                        <h3 className={`text-xs font-bold font-heading leading-tight line-clamp-2 text-white`}>
                                            {module.title}
                                        </h3>
                                        {!isLocked && (
                                            <div className="flex items-center gap-1 mt-1.5 overflow-hidden">
                                                <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/10 backdrop-blur-sm">
                                                    <Clock size={10} className="text-white/80" />
                                                    <span className="text-[9px] text-white/80 font-body">
                                                        {module.duration_minutes} {t('ui.common.min')}
                                                    </span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Card>
                            </motion.div>
                        );
                    })}
                </div>
            </div>

            <BottomNavigation />
        </MobileContainer>
    );
}
